import { useMemo } from 'react';
import { Optional } from 'ts-toolbelt/out/Object/Optional';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { useThreadHeaderContext } from '@frontend/thread-header-context';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { AssociatedContactsListItem } from '../associated-contacts-list-item';
import { ThreadDrawerProps } from '../thread-drawer';
import { ThreadPopoverDrawer } from '../thread-popover-drawer';

type AssociatedContactsDrawerProps = Optional<
  Omit<ThreadDrawerProps, 'children' | 'closeLabel'>,
  'onClose' | 'open'
> & {
  onSelect: (personId: string) => void;
  onChangePrimaryContact?: ({
    personId,
    firstName,
    lastName,
    preferredName,
  }: {
    personId: string;
    firstName: string;
    lastName: string;
    preferredName: string;
  }) => void;
};

export const AssociatedContactsDrawer = ({
  onSelect,
  onChangePrimaryContact,
  onClose,
  open,
  ...rest
}: AssociatedContactsDrawerProps) => {
  const { t } = useTranslation('thread-header');
  const {
    groupId,
    personPhone,
    primaryContactId,
    associatedContacts,
    associatedContactsDrawerIsOpen,
    setAssociatedContactsDrawerIsOpen,
  } = useThreadHeaderContext();
  const { mutate } = PersonsV3.PersonMutations.useUpsertPrimaryContactLegacyMutation({
    onSuccess: (_, req) => {
      const person = associatedContacts?.find((p) => p.personId === req.personId);
      if (!person) return;
      onChangePrimaryContact?.({
        personId: person.personId,
        firstName: person.firstName ?? '',
        lastName: person.lastName ?? '',
        preferredName: person.preferredName ?? '',
      });
    },
  });
  const persons = associatedContacts || [];

  const resolvedOpen = useMemo(() => open || associatedContactsDrawerIsOpen, [open, associatedContactsDrawerIsOpen]);

  return (
    <ThreadPopoverDrawer
      open={resolvedOpen}
      onClose={() => {
        setAssociatedContactsDrawerIsOpen(false);
        onClose?.();
      }}
      closeLabel={t('Close')}
      {...rest}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(1, 4, 1, 2),
          borderBottom: `1px solid ${theme.colors.neutral10}`,
        }}
      >
        <Button
          iconName='back'
          variant='secondary'
          onClick={() => {
            setAssociatedContactsDrawerIsOpen(false);
            onClose?.();
          }}
        />
        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Text size='large' weight='bold'>
            {t('Contacts Under This Number')}
          </Text>
          <Text size='medium' color='light'>
            {t('Select to focus another contact...')}
          </Text>
        </div>
        <Text color='light'>{associatedContacts?.length ?? 1}</Text>
      </div>
      <ul
        css={{
          width: '100%',
          listStyle: 'none',
          margin: 0,
          paddingInlineStart: 0,
          maxHeight: '40vh',
          overflowY: 'auto',
        }}
      >
        {persons.map((person) => (
          <AssociatedContactsListItem
            key={person.personId}
            personId={person.personId}
            person={person}
            onSelect={() => {
              onSelect?.(person.personId);
              setAssociatedContactsDrawerIsOpen(false);
              onClose?.();
            }}
            onSetPrimaryContact={() => {
              mutate({ personId: person.personId, locationId: groupId, phoneNumber: personPhone });
              setAssociatedContactsDrawerIsOpen(false);
              onClose?.();
            }}
            isPrimary={person.personId === primaryContactId}
            locationId={groupId}
            size='small'
          />
        ))}
      </ul>
    </ThreadPopoverDrawer>
  );
};
